import { alpha, Components, createTheme, PaletteOptions, SxProps, Theme } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    custom?: {
      background?: {
        transparent: {
          white: string;
          black: string;
        };
      };
      highlight?: {
        main: string;
      };
    };

    softContrast?: Palette['primary'];
  }
  interface PaletteOptions {
    custom?: {
      background?: {
        transparent: {
          white: string;
          black: string;
        };
      };
    };
    softContrast?: PaletteOptions['primary'];
  }
}

const themeColors: Partial<PaletteOptions> = {
  primary: {
    main: '#be62a5',
    light: '#d8a4ca',
  },
  secondary: {
    main: '#6b7c93',
    light: '#d8dce2',
  },
  warning: {
    main: '#f3bb45',
    light: '#f9d79c',
  },
  error: {
    main: '#d32f2f',
    light: '#f79999',
    dark: '#a85959',
  },
  softContrast: {
    light: '#F9F9FB',
    main: '#D8DCE2',
    contrastText: '#6B7C93',
  },
};

const customStyles = {
  background: {
    transparent: {
      white: 'rgba(255, 255, 255, 0.5)',
      black: 'rgba(0, 0, 0, 0.5)',
    },
  },
  highlight: {
    main: '#1976d2',
  },
};

const themeTypography = {
  typography: {
    fontFamily: 'Ubuntu, sans-serif',
    h1: {
      fontSize: 32,
      fontWeight: 700,
    },
    h2: {
      fontSize: 24,
      fontWeight: 700,
    },
    h3: {
      fontSize: 18,
      fontWeight: 700,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 700,
    },
    h5: {
      fontSize: '0.8rem',
    },
    subtitle1: {
      fontSize: 16,
    },
  },
};

export const getTranslucentSurfaceStyles = (theme: Theme) => ({
  backgroundImage:
    theme.palette.mode === 'light'
      ? `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.92)} 0%, ${alpha(
          theme.palette.common.white,
          0.68
        )} 100%)`
      : `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.045)} 0%, ${alpha(
          theme.palette.common.white,
          0.008
        )} 100%)`,
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  backgroundColor:
    theme.palette.mode === 'light' ? alpha(theme.palette.common.white, 0.84) : alpha(theme.palette.common.black, 0.78),
  boxShadow: theme.palette.mode === 'light' ? '0 18px 40px rgba(15, 23, 42, 0.08)' : '0 22px 52px rgba(0, 0, 0, 0.42)',
});

export const getDashboardCanvasStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.background.default,
  backgroundImage:
    theme.palette.mode === 'light'
      ? 'none'
      : `radial-gradient(circle at top, ${alpha(
          theme.palette.common.white,
          0.03
        )} 0%, transparent 48%), linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.012)} 0%, ${alpha(
          theme.palette.common.black,
          0.26
        )} 100%)`,
});

export const getDashboardCardSurfaceStyles = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: [
          `radial-gradient(140% 120% at 0% 0%, ${alpha(theme.palette.common.white, 0.14)} 0%, ${alpha(
            theme.palette.common.white,
            0.055
          )} 24%, ${alpha(theme.palette.common.white, 0.012)} 46%, transparent 68%)`,
          `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.06)} 0%, ${alpha(
            theme.palette.common.white,
            0.016
          )} 100%)`,
        ].join(', '),
      }
    : {};

export const getViewerOverlaySurfaceStyles = (theme: Theme) => ({
  ...getTranslucentSurfaceStyles(theme),
  backgroundColor:
    theme.palette.mode === 'light' ? alpha(theme.palette.common.white, 0.64) : alpha(theme.palette.common.black, 0.7),
  color: theme.palette.text.primary,
  border: 'none',
  boxShadow: theme.palette.mode === 'light' ? '0 10px 24px rgba(15, 23, 42, 0.08)' : '0 14px 32px rgba(0, 0, 0, 0.32)',
});

const components: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      html: {
        minHeight: '100%',
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.between('md', 'xl')]: {
          fontSize: '15px',
        },
      },
      body: {
        minHeight: '100%',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: theme.transitions.duration.shorter,
        }),
      },
      '#root': {
        minHeight: '100vh',
      },
    }),
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => getTranslucentSurfaceStyles(theme),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...getTranslucentSurfaceStyles(theme),
        overflow: 'hidden',
      }),
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      title: {
        fontSize: 16,
        fontWeight: 700,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: 20,
        minWidth: 600,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontSize: 14,
        fontWeight: 700,
        textTransform: 'none',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        maxWidth: 300,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) =>
        theme.palette.mode === 'dark'
          ? {
              backgroundColor: alpha(theme.palette.common.black, 0.82),
              backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.045)} 0%, ${alpha(
                theme.palette.common.white,
                0.035
              )} 100%)`,
              '& .MuiPaper-root': {
                backgroundColor: alpha(theme.palette.common.black, 0.36),
                backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.09)} 0%, ${alpha(
                  theme.palette.common.white,
                  0.075
                )} 100%)`,
                outline: `1px solid ${alpha(theme.palette.common.white, 0.09)}`,
                outlineOffset: -1,
              },
            }
          : {},
    },
    defaultProps: {
      PaperProps: {
        onClick: (e: any) => {
          e.stopPropagation();
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...themeTypography,
  shape: { borderRadius: 16 },
  palette: {
    mode: 'light',
    ...themeColors,
    custom: customStyles,
  },
  components: {
    ...components,
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiSelect: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectRoot: {
          width: 'auto',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  ...themeTypography,
  shape: { borderRadius: 16 },
  palette: {
    mode: 'dark',
    ...themeColors,
    background: {
      default: '#000000',
      paper: '#0d0d0d',
    },
    custom: customStyles,
  },
  components: {
    ...components,
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiSelect: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectRoot: {
          width: 'auto',
        },
      },
    },
  },
});

export const defaultLayerColor = '#0000FF';

export const defaultLayerColors = [
  '#FFFF00',
  '#FF7600',
  '#00FFFF',
  '#FF00FF',
  '#00FF00',
  '#002DB3',
  '#B3B300',
  '#FF0000',
  '#BEEEDB',
  '#AA00FF',
  '#80FFBF',
  '#BFCFFF',
  '#EEEE80',
  '#FF8080',
  '#26DBFF',
  '#7700B3',
  '#80FF80',
  '#809FFF',
  '#D99926',
  '#B30000',
  '#00B3B3',
  '#EABFFF',
  '#00B300',
  '#00209F',
  '#DEEEAF',
  '#D92676',
  '#80EEEE',
  '#D580FF',
  '#4CDB00',
  '#0062AD',
  '#B9D9C3',
  '#B36200',
  '#BFE6FF',
  '#7E75AA',
  '#9CAD9C',
  '#6262FF',
  '#626200',
  '#FFC680',
  '#5A8080',
  '#E7B6FF',
  '#5A805A',
  '#BFD9FF',
  '#808062',
  '#D9B362',
  '#ACBFBF',
  '#7B7380',
  '#BFFFE6',
  '#ACB1BF',
  '#FFE2BF',
  '#E6BFBF',
  '#758282',
  '#B9ACBF',
  '#ACBFAC',
  '#BFBFBF',
  '#676767',
  defaultLayerColor,
];

export const getPermanentMacStyleScrollbarStyles = (theme: Theme, applyToChildren?: boolean): SxProps => ({
  // Show a persistent scrollbar for the data grid
  [`&${applyToChildren ? ' ' : ''}::-webkit-scrollbar`]: {
    width: 8 /* Adjust width as needed */,
    height: 8 /* For horizontal scrollbars */,
  },

  /* For the scrollbar thumb (the draggable handle) */
  [`&${applyToChildren ? ' ' : ''}::-webkit-scrollbar-thumb`]: {
    boxShadow: 1,
    /* Semi-transparent black/gray */
    backgroundColor: theme.palette.mode == 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.4)',
    /* Rounded corners */
    borderRadius: 10,

    /* On hover for the thumb */
    [`&${applyToChildren ? ' ' : ''}::-webkit-scrollbar-thumb:hover`]: {
      backgroundColor: theme.palette.mode == 'light' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.6)',
    },
  },
});
